// Tour script - slideshow walkthrough of NJRC.
//
// Engine note: every step is a self-contained slide on a deep backdrop.
// The engine no longer DOM-tracks live elements - `selector` and
// `interaction` exist on the type for back-compat but are not used.
// What matters per step:
//   • title    - 4-7 words, lands word-by-word
//   • body     - 25-60 words, supports **bold** and *italic* via markdown
//   • illustration - keyed to step.id (or override) → renders a stylized
//                    mockup of the feature being taught. Add a new switch
//                    case in tour.tsx Illustration() when adding new ids.
//
// Sequence shape (15 steps):
//   1-3   Cinematic open: welcome + mission + capabilities
//   4     Map of the app - five tabs + two assistants
//   5-6   The two power tools - ⌘K palette + Ask Jack AI orb
//   7-14  Per-page deep dive: Today, Lookup, People, Person hero,
//         Person records, Actions menu, Stats, How To
//   15    Outro - call to action + re-run anytime

export interface TourStep {
  id: string;
  /** Optional illustration kind override. Defaults to step `id` -
   *  the Illustration component switches on this to render the right
   *  per-step stylized art. */
  illustration?: string;
  /** Step kind - "intro" and "outro" are full-screen narrative stages
   *  with the orb at center and orchestrated typography sequence.
   *  Default ("normal") is the slideshow teaching stage. */
  kind?: "normal" | "intro" | "outro";
  /** Optional eyebrow text override (default is "Chapter N"). */
  eyebrow?: string;
  /** Route to be on when this step shows. Engine navigates if different
   *  so the user lands in the right place if they hit Esc mid-tour. */
  route?: string;
  /** Legacy - unused by the slideshow engine; kept for type back-compat. */
  selector?: string;
  /** Legacy - unused by the slideshow engine; kept for type back-compat. */
  beforeShow?: () => void | Promise<void>;
  title: string;
  body: string;
  /** Legacy - unused by the slideshow engine; kept for type back-compat. */
  highlight?: "soft" | "strong" | "none";
  /** Legacy - unused by the slideshow engine; kept for type back-compat. */
  interaction?:
    | { kind: "click"; selector: string; prompt: string }
    | { kind: "navigate"; routePattern: string; prompt: string };
}

export const TOUR_STEPS: TourStep[] = [
  // ─── 1. Welcome (cinematic intro stage) ──────────────────────────
  {
    id: "welcome",
    kind: "intro",
    eyebrow: "NJRC Legal Services Tool",
    route: "/today",
    title: "An entire legal team, in one app.",
    body:
      "We pull every NJ court record. Generate every letter and filing. Run live participant meetings with auto-transcription and one-click profile updates. The next three minutes will show you everything.",
  },

  // ─── 2. Why this exists (cinematic intro stage) ──────────────────
  {
    id: "mission",
    kind: "intro",
    eyebrow: "Why this exists",
    route: "/today",
    title: "Free people, faster.",
    body:
      "NJRC helps people coming home from incarceration recover their lives - clean fines, recall warrants, restore licenses, expunge old crimes, find work. This tool collapses what used to take a paralegal hours into a few clicks.",
  },

  // ─── 3. Big-picture capabilities ─────────────────────────────────
  // The "what can this thing actually do" answer, before we dive into
  // the per-page tour. Six concrete capabilities laid out as a grid.
  {
    id: "capabilities",
    eyebrow: "What it does",
    route: "/today",
    title: "Everything a paralegal does.",
    body:
      "Pull records from every NJ court, the DOC, and the MVC. Draft court letters. Transcribe live meetings. Generate resumes. Bundle expungement packets. All in one place - no more juggling six tabs and a paper folder.",
  },

  // ─── 4. Map of the app - top nav overview ────────────────────────
  // Refreshed for the current single-row nav: 5 verbs + ⌘K + Tour +
  // Ask Jack orb + avatar. Story now lives under the avatar dropdown.
  {
    id: "nav-overview",
    eyebrow: "The map",
    route: "/today",
    title: "Five tabs. Two assistants.",
    body:
      "**Today** is your morning brief. **Lookup** adds a new participant. **People** is the full roster. **Stats** is the analytics. **How To** is the staff handbook. The right cluster has **⌘K** (universal search) and the **Ask Jack** AI orb. Every shortcut is one click away.",
  },

  // ─── 5. ⌘K command palette ───────────────────────────────────────
  // Critical "you must know this" feature. Lives globally so it works
  // from any page; the only learnable shortcut in the whole app.
  {
    id: "cmd-k",
    eyebrow: "Power tool #1",
    route: "/today",
    title: "⌘K finds anything in two keystrokes.",
    body:
      "Hit **⌘K** from anywhere. The universal command palette opens. Type a name to jump to a person. Type a topic to open a how-to. Type a verb to navigate. It searches *across* People, the manual, and quick actions - one input, every destination.",
  },

  // ─── 6. Ask Jack AI orb ──────────────────────────────────────────
  // The other power tool: an LLM trained on the LSM 2026 corpus, NJRC-
  // scoped (refuses off-topic), accessible from every page.
  {
    id: "ai-orb",
    eyebrow: "Power tool #2",
    route: "/today",
    title: "Ask Jack, the in-house AI.",
    body:
      "The blue orb in the top right opens an NJRC-scoped chatbot. Trained on the entire **Legal Services Manual 2026** - ask any *how do I…* question and get the answer with citations to the source module. It refuses anything off-topic, so you can trust where the answer came from.",
  },

  // ─── 7. Today page ───────────────────────────────────────────────
  {
    id: "today",
    eyebrow: "Each morning",
    route: "/today",
    title: "Today is your morning brief.",
    body:
      "An LLM writes a fresh paragraph each morning about your caseload - who has court today, what's overdue, what changed overnight. Below that: pinned people, upcoming dates, and the work waiting on you. *To get here:* click **Today** in the top nav (or just open the app - it's home).",
  },

  // ─── 8. Lookup page ──────────────────────────────────────────────
  {
    id: "lookup",
    eyebrow: "When a participant comes in",
    route: "/lookup",
    title: "Adding a new participant takes 20 seconds.",
    body:
      "Type their name and date of birth. Hit **Run search**. A dozen background scrapes kick off in parallel - every NJ court, the DOC, the MVC, plus state databases. By the time you finish paperwork, the profile is built. *To get here:* click **Lookup** in the top nav.",
  },

  // ─── 9. People list ──────────────────────────────────────────────
  // Updated for the new card grid: havoc-tier color gradient (green at
  // 0 → bordeaux at 90+) plus per-card crime-type icon watermarks.
  {
    id: "people-list",
    eyebrow: "Your roster",
    route: "/records",
    title: "Color-coded by chaos.",
    body:
      "Each card is one person. Cards are colored by their **Havoc Score** - calm green at the bottom of the scale, full bordeaux at the top. Faint icons across each card hint at the kinds of crimes that person's record contains. Switch to a table view from the toggle in the upper right.",
  },

  // ─── 10. PersonPage hero ─────────────────────────────────────────
  // Bordeaux hero with name + havoc + tier pill + AI nickname/bio +
  // thumbtack pin. Replaces the old "card style" hero.
  {
    id: "person-hero",
    eyebrow: "The participant page",
    title: "The whole picture, at a glance.",
    body:
      "Name in giant Fraunces serif. The big number on the right is the **Havoc Score** with its tier label below. An AI-written nickname and one-line bio sit above. The thumbtack icon pins this person to your Today page. Every section that follows is one click away from being a downloadable packet.",
  },

  // ─── 11. Records section ─────────────────────────────────────────
  {
    id: "person-records",
    eyebrow: "The data",
    title: "Every charge ever pulled.",
    body:
      "Each row is a case - court, year, charge, status. *Click any row* to drill into the detail page with an AI hot-take on what likely went down. Above the records, the **Scrapes** panel shows which sources finished, which are still running, and which need more identifiers from you.",
  },

  // ─── 12. Actions menu - the export menagerie ─────────────────────
  // Combined "spotlight on the button" + "what's inside" into one slide
  // since the slideshow doesn't track DOM. Mentions Live meetings as
  // one of the leaves so it gets a mention without its own slide.
  {
    id: "actions",
    eyebrow: "The big idea",
    title: "One button. Hours of work, gone.",
    body:
      "The **Actions** button at the bottom of every person's page opens the export menu. **For the participant:** resumes, handouts, and *Start a meeting* for live AI-transcribed sessions. **Court documents:** letters, expungement packets, MVC restoration filings. **Internal:** print summaries and full-record ZIPs. Every document pre-fills with the participant's data.",
  },

  // ─── 13. Stats ───────────────────────────────────────────────────
  // Updated for the new chapter-section frontispieces + LLM aggregate
  // commentary at the top of the page.
  {
    id: "stats",
    eyebrow: "The view from above",
    route: "/stats",
    title: "Every chart. Every pattern.",
    body:
      "Maps of where participants come from. Sankey flows of how cases move between courts. Severity distributions. Demographics. The top card opens with an LLM-written paragraph naming the headlines and the surprises. Useful for grant writing, board reports, and seeing the caseload's actual shape.",
  },

  // ─── 14. How To ──────────────────────────────────────────────────
  {
    id: "how-to",
    eyebrow: "When you get stuck",
    route: "/how-to",
    title: "The handbook lives here.",
    body:
      "Step-by-step guides for every form, court, and edge case - straight from the **Legal Services Manual 2026**. Includes an interactive map of every NJ municipal court with the clerk's contact info, plus the entire birth-certificate maze organized by state and country. Same answers Ask Jack draws from, just browsable.",
  },

  // ─── 15. Wrap-up (cinematic outro) ───────────────────────────────
  {
    id: "wrap",
    kind: "outro",
    eyebrow: "You're ready",
    title: "That's the tour.",
    body:
      "Re-run this anytime from the **Tour** link up top. Hit **⌘K** from anywhere to find a person or topic. Click the blue orb to ask Jack. Welcome to NJRC.",
  },
];
